import { useEffect, useState } from "react";
import { Instagram } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../api";
import { SITE } from "../data/siteData";

export const ytId = (url) => {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

export const GalleryTile = ({ item, testid }) => {
  if (item.type === "youtube") {
    const id = ytId(item.url);
    if (!id) return null;
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 aspect-video bg-black" data-testid={testid}>
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${id}`}
          title={item.caption || "Chinook K9 training video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }
  if (item.type === "video") {
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 aspect-video bg-black" data-testid={testid}>
        <video className="w-full h-full object-cover" src={item.url} controls playsInline preload="metadata" />
      </div>
    );
  }
  return (
    <div className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-square" data-testid={testid}>
      <img src={item.url} alt={item.caption || "Chinook K9 training"} loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      {item.caption && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-white text-sm font-medium">{item.caption}</p>
        </div>
      )}
    </div>
  );
};

export default function Gallery() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="gallery-page">
      <SEO
        title="Gallery | Training Photos & Videos | Chinook K9 Calgary"
        description="Photos and videos from Chinook K9 board & train programs, private lessons and puppy jumpstart sessions minutes from Calgary, AB."
        path="/gallery"
      />
      <section className="bg-obsidian pt-40 pb-16 border-b border-white/10" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4">Gallery</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white">
              Training in action
            </h1>
            <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
              Real dogs, real results — photos and videos from our board & train programs, private lessons and jumpstart sessions.
            </p>
            <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="gallery-instagram-cta"
              className="inline-flex items-center gap-2 mt-6 text-brand font-heading font-bold text-sm hover:underline">
              <Instagram size={17} /> Follow @chinookk9 for daily training clips
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20" data-testid="gallery-grid-section">
        <div className="max-w-7xl mx-auto px-6">
          {!items ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-white/5 rounded-2xl" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="text-white/50 text-center py-16" data-testid="gallery-empty">No gallery items yet — check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {items.map((item, i) => (
                <Reveal key={item.item_id} delay={(i % 3) * 0.08}>
                  <GalleryTile item={item} testid={`gallery-item-${i}`} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
